import { useApolloClient, useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  GetUserDetailDocument,
  GetUserForUpdateDocument,
  GetUsersDocument,
  UpdateUserDocument,
  type UpdateUserMutation,
  type UpdateUserMutationVariables,
} from "~/features/admin/users/gqls";
import type { ProductRoleAssignment, UserEditFormData } from "~/features/admin/users/types";
import { translateError } from "~/lib/errors";

interface UseUserEditOptions {
  onSuccess?: (data: UserEditFormData) => void;
  initialRoles?: Array<{ productId: string; roleId: string }>;
}

export function useUserEdit({ onSuccess, initialRoles }: UseUserEditOptions = {}) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [processing, setProcessing] = useState(false);
  const [updateUser] = useMutation<UpdateUserMutation, UpdateUserMutationVariables>(UpdateUserDocument);

  const [selectedRoles, setSelectedRoles] = useState<Map<string, ProductRoleAssignment>>(new Map());

  useEffect(() => {
    if (initialRoles) {
      const map = new Map<string, ProductRoleAssignment>();
      for (const r of initialRoles) {
        map.set(r.productId, r);
      }
      setSelectedRoles(map);
    }
  }, [initialRoles]);

  const setRoleForProduct = useCallback((productId: string, roleId: string | null) => {
    setSelectedRoles((prev) => {
      const next = new Map(prev);
      if (roleId === null || roleId === "") {
        next.delete(productId);
      } else {
        next.set(productId, { productId, roleId });
      }
      return next;
    });
  }, []);

  const submit = useCallback(
    async (formData: UserEditFormData) => {
      setProcessing(true);
      try {
        await updateUser({
          variables: {
            input: {
              id: formData.id,
              displayName: formData.displayName,
              email: formData.email,
              productRoleAssignments: Array.from(selectedRoles.values()),
              userSegmentIds: formData.userSegmentIds,
              userGroupIds: formData.userGroupIds || [],
              memo: formData.memo,
            },
          },
          refetchQueries: [GetUsersDocument, { query: GetUserDetailDocument, variables: { userId: formData.id } }],
        });
        toast.success(t("feature.common.updatedSuccessfully", { model: "user" }));
        onSuccess?.(formData);
      } catch (e) {
        if (e instanceof Error && /User [\da-f-]+ was saved, but COTEN ID login preparation failed/.test(e.message)) {
          // 一覧を開いていない場合も、再編集で保存前のメモ・権限を上書きしないようにする。
          await Promise.allSettled([
            client
              .query({ query: GetUserDetailDocument, variables: { userId: formData.id }, fetchPolicy: "network-only" })
              .catch(() =>
                client.cache.evict({ id: "ROOT_QUERY", fieldName: "cnutGetUser", args: { id: formData.id } }),
              ),
            client
              .query({ query: GetUserForUpdateDocument, variables: { id: formData.id }, fetchPolicy: "network-only" })
              .catch(() => client.cache.evict({ id: "ROOT_QUERY", fieldName: "usersByPk", args: { id: formData.id } })),
            client.refetchQueries({ include: [GetUsersDocument] }),
          ]);
          toast.error(t("feature.users.loginPreparationFailed"));
          onSuccess?.(formData);
        } else {
          toast.error(translateError(t, e));
        }
      } finally {
        setProcessing(false);
      }
    },
    [updateUser, client, selectedRoles, t, onSuccess],
  );

  return { submit, processing, selectedRoles, setRoleForProduct };
}
